// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC, useContext } from "react";

const enum fields {
    showImage = "showImage",
    showContent = "showContent",
}

interface Props extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showContent]: boolean;
    };
}

export interface BrandDetailsFeaturedImageAndContentStyles {
    container?: GridContainerProps;
    imageItem?: GridItemProps;
}

export const brandDetailsFeaturedImageAndContent: BrandDetailsFeaturedImageAndContentStyles = {
    container: {
        gap: 16,
    },
    imageItem: {
        width: [12, 4, 4, 3, 3],
    },
};

const styles = brandDetailsFeaturedImageAndContent;

const BrandDetailsFeaturedImageAndContent: FC<Props> = ({ fields: { showImage, showContent } }) => {
    const { value: brand } = useContext(BrandStateContext);
    if (!brand || ((!showImage || !brand.featuredImagePath) && (!showContent || !brand.htmlContent))) {
        return null;
    }

    return (
        <GridContainer data-test-selector="brandFeaturedImageAndContent" {...styles.container}>
            {showImage && brand.featuredImagePath && (
                <GridItem {...styles.imageItem}>
                    {createWidgetElement("BrandDetails/BrandDetailsImage", {
                        fields: { showImage: true, showLinks: false },
                    })}
                </GridItem>
            )}
            {showContent && brand.htmlContent && (
                <GridItem width={showImage && brand.featuredImagePath ? [12, 8, 8, 9, 9] : 12}>
                    {createWidgetElement("BrandDetails/BrandDetailsContent", { fields: {} })}
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: BrandDetailsFeaturedImageAndContent,
    definition: {
        group: "Brand Details",
        icon: "image",
        displayName: "Brand Featured Image And Content",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showImage,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showContent,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
